import { useEffect, useState, useRef, useContext } from "react";
import { ChatContext } from '../../../services/chatContext';
import { Col, Row } from "react-bootstrap";
import ChatWindow from "./ChatWindow";
import ChatList from "./ChatList";
import { getAllAwaitingChats, adviserJoinChat, getChatMessages } from "../../../services/chatService";
import { refreshAccessToken, getAccessToken } from "../../../services/authService";
import * as signalR from "@microsoft/signalr";
import "../../../scss/components/liveChat.scss";

const AllChats = () => {
    const { chats } = useContext(ChatContext);

    const [awaitingChats, setAwaitingChats] = useState([]);
    const [openChats, setOpenChats] = useState([]);
    const [minimisedChats, setMinimisedChats] = useState(new Set());
    const [activeChatId, setActiveChatId] = useState(null);
    const [messages, setMessages] = useState({});
    const [joiningChatId, setJoiningChatId] = useState(null);

    // Keep the connection in a ref so re-renders don�t blow it away
    const chatConnectionRef = useRef(null);
    const chatRef = useRef({});

    useEffect(() => {
        let mountCount = window.__chatMountCount || 0;
        mountCount++;
        window.__chatMountCount = mountCount;
    }, []);


    // -------------------------------------------------------
    // Fetch awaiting chats (unchanged)
    // -------------------------------------------------------
    useEffect(() => {
        if (!chats || chats.length === 0) {
            const fetchChats = async () => {
                const awaiting = await getAllAwaitingChats();
                setAwaitingChats(awaiting || []);
            };
            fetchChats();
        } else {
            setAwaitingChats(chats);
        }
    }, [chats]);

    // -------------------------------------------------------
    // Helper to get a access token
    // -------------------------------------------------------
    const GetAccessToken = async () => {
        const accessToken = await getAccessToken();

        return accessToken;
    };

    // -------------------------------------------------------
    // Create ONE SignalR connection on mount (to /chathub)
    // -------------------------------------------------------
    useEffect(() => {
        let isMounted = true;
        const connect = async () => {
            const connection = new signalR.HubConnectionBuilder()
                .withUrl(`${process.env.REACT_APP_API_URL}/chathub`, {
                    accessTokenFactory: GetAccessToken,
                    transport: signalR.HttpTransportType.WebSockets
                })
                .withAutomaticReconnect()
                .build();

            // ---- ReceiveMessage ----
            connection.on("ReceiveMessage", (chatId, message, firstName) => {

                if (!chatId) return; // nowt we can do

                setMessages(prevMessages => {
                    const updated = {
                        ...prevMessages,
                        [chatId]: [
                            ...(prevMessages[chatId] || []),
                            { userFirstName: firstName, message, messageType: "text" }
                        ]
                    };

                    // Scroll after DOM update
                    setTimeout(() => {
                        const ref = chatRef.current[chatId];
                        if (ref && typeof ref.scrollToBottom === "function") {
                            ref.scrollToBottom();
                        }
                    }, 100);

                    return updated;
                });
            });

            // ---- ReceiveImage ---- (ideally: chatId, imageString, firstName)
            connection.on("ReceiveImage", (chatId, imageString, firstName) => {

                if (!chatId) return;

                setMessages(prevMessages => {
                    const updated = {
                        ...prevMessages,
                        [chatId]: [
                            ...(prevMessages[chatId] || []),
                            { userFirstName: firstName, message: imageString, messageType: "image" }
                        ]
                    };

                    setTimeout(() => {
                        const ref = chatRef.current[chatId];
                        if (ref && typeof ref.scrollToBottom === "function") {
                            ref.scrollToBottom();
                        }
                    }, 100);

                    return updated;
                });
            });

            try {
                await connection.start();
                if (!isMounted) {
                    await connection.stop();
                    return;
                }
                chatConnectionRef.current = connection;
            } catch (err) {
                console.error("SignalR connection error:", err);
            }
        };

        connect();

        return () => {
            isMounted = false;
            if (chatConnectionRef.current) {
                chatConnectionRef.current.stop().catch(err => console.error("Error stopping SignalR connection:", err));
            }
        };
    }, []);

    // -------------------------------------------------------
    // Join a chat
    // -------------------------------------------------------
    const joinChat = async (chatId) => {
        setJoiningChatId(chatId);

        try {
            await adviserJoinChat(chatId);

            const batchNumber = 0;
            const chatHistory = await getChatMessages(chatId, batchNumber);

            setMessages(prev => ({
                ...prev,
                [chatId]: chatHistory || []
            }));

            const connection = chatConnectionRef.current;
            if (!connection) throw new Error("SignalR not connected yet.");

            // Tell hub we want to join this chat
            try {
                await connection.invoke("OnJoinAsync", chatId);
            } catch (err) {
                console.log(err);
            }

            // Move into open chats list
            if (!openChats.some(chat => chat.id === chatId)) {
                const chatToOpen = awaitingChats.find(chat => chat.id === chatId);
                if (chatToOpen) {
                    setOpenChats(prev => [...prev, { ...chatToOpen, chatId }]);
                }
            }

            setActiveChatId(chatId);


        } catch (error) {
            console.error("Error joining chat:", error);
        } finally {
            setJoiningChatId(null);
        }
    };

    // -------------------------------------------------------
    // Send a message to the CURRENTLY JOINED chat on the server
    // (server knows which via Context.Items["ChatId"])
    // -------------------------------------------------------
    const handleSendMessage = async (message) => {
        const connection = chatConnectionRef.current;
        if (!connection || !activeChatId) return;

        try {
            await connection.invoke("SendMessageToGroupAsync", message, activeChatId);

            setMessages(prev => {
                const updated = {
                    ...prev,
                    [activeChatId]: [
                        ...(prev[activeChatId] || []),
                        { user: "Advisor", message }
                    ]
                };

                setTimeout(() => {
                    const ref = chatRef.current[activeChatId];
                    if (ref && typeof ref.scrollToBottom === "function") {
                        ref.scrollToBottom();
                    }
                }, 100);

                return updated;
            });
        } catch (err) {
            console.error("Send failed:", err);
        }
    };

    // -------------------------------------------------------
    // Close (leave) a chat window (OnLeaveAsync)
    // -------------------------------------------------------
    const closeChatWindow = async (id) => {
        const connection = chatConnectionRef.current;

        try {
            await connection?.invoke("OnLeaveAsync", id);
        } catch (err) {
            console.error("Error during OnLeaveAsync:", err);
        }

        setOpenChats(prev => prev.filter(chat => chat.id !== id));
        if (activeChatId === id) {
            setActiveChatId(null);
        }
    };

    // -------------------------------------------------------
    // Release a chat (OnReleaseAsync) and close it
    // -------------------------------------------------------
    const releaseChatAndCloseChatWindow = async (id) => {
        const connection = chatConnectionRef.current;

        try {
            await connection?.invoke("OnReleaseAsync", id);
        } catch (err) {
            console.error("Error during OnReleaseAsync:", err);
        }

        setOpenChats(prev => prev.filter(chat => chat.id !== id));
        if (activeChatId === id) {
            setActiveChatId(null);
        }
    };

    return (
        <>
            <div className="pendingChats component">
                <Row>
                    <Col xs={12}>
                        <h3>
                            Live Chat Requests{" "}
                            <span style={{ fontSize: "14px" }}>
                                - {awaitingChats.length} chat(s) awaiting advisor
                            </span>
                        </h3>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <ChatList
                            awaitingChats={awaitingChats}
                            joinChat={joinChat}
                            joiningChatId={joiningChatId}
                            openChats={openChats}
                        />
                    </Col>
                </Row>
            </div>

            {openChats.map(chat => (
                <ChatWindow
                    key={chat.id}
                    ref={(el) => {
                        chatRef.current[chat.id] = el;
                    }}
                    chat={chat}
                    isActive={chat.id === activeChatId}
                    bringToFront={setActiveChatId}
                    closeChat={closeChatWindow}
                    releaseChat={releaseChatAndCloseChatWindow}
                    toggleMinimise={() =>
                        setMinimisedChats(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(chat.id)) {
                                newSet.delete(chat.id);
                            } else {
                                newSet.add(chat.id);
                            }
                            return newSet;
                        })
                    }
                    isMinimised={minimisedChats.has(chat.id)}
                    sendMessage={handleSendMessage}
                    messages={messages[chat.id] || []}
                />
            ))}
        </>
    );
};

export default AllChats;
